
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import styles from '../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'Onboarding guide',
  layout: 'guide',
  description: 'What to expect when building, testing, launching, and operating a Core Exchange integration',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='Onboarding guide' subtitle={metadata.description} mdxType="Header" />
      <h2 {...{
        "id": "about-core-exchange"
      }}>{`About Core Exchange`}</h2>
      <p>{`Plaid Core Exchange is an FDX-aligned API specification scoped to the data models and endpoints that power Plaid downstream products. This keeps implementation focused on real-world use cases while remaining aligned with the FDX standard.`}</p>
      <p>{`Core Exchange is designed for reliability, scale, and ecosystem interoperability rather than bespoke, one-off integrations. Once live, your institution becomes available to the full Plaid ecosystem of applications.`}</p>
      <h2 {...{
        "id": "the-high-level-journey"
      }}>{`The high-level journey`}</h2>
      <h3 {...{
        "id": "onboarding-and-setup"
      }}>{`Onboarding and setup`}</h3>
      <ul>
        <li parentName="ul">{`Get access to the `}<a parentName="li" {...{
            "href": "/core-exchange/docs/dashboard-overview"
          }}>{`Data Partner Dashboard`}</a></li>
        <li parentName="ul">{`Configure environments, branding, authentication, and endpoints`}</li>
      </ul>
      <h3 {...{
        "id": "build-and-test"
      }}>{`Build and test`}</h3>
      <ul>
        <li parentName="ul">{`Test authentication and data endpoints in the `}<a parentName="li" {...{
            "href": "/core-exchange/docs/testing"
          }}>{`Validator`}</a></li>
        <li parentName="ul">{`Iterate on validation failures and errors`}</li>
      </ul>
      <h3 {...{
        "id": "launch"
      }}>{`Launch`}</h3>
      <ul>
        <li parentName="ul">{`Request `}<a parentName="li" {...{
            "href": "/core-exchange/docs/production-access"
          }}>{`production access`}</a></li>
        <li parentName="ul">{`Optionally run stealth testing in production against real-world apps`}</li>
        <li parentName="ul">{`Launch broadly across the Plaid ecosystem`}</li>
      </ul>
      <h3 {...{
        "id": "post-launch"
      }}>{`Post-launch`}</h3>
      <ul>
        <li parentName="ul"><a parentName="li" {...{
            "href": "#migrations"
          }}>{`Migrate existing users`}</a>{`, if applicable`}</li>
        <li parentName="ul">{`Monitor health and transition to `}<a parentName="li" {...{
            "href": "/core-exchange/docs/post-launch"
          }}>{`steady-state support`}</a></li>
      </ul>
      <p>{`For platform integrations, some steps are completed once, while others repeat each time a new institution is added. Each institution added to a platform follows its own configuration, testing, and production launch lifecycle.`}</p>
      <p>{`While some partners launch in as little as one week, integrations can take six to eight weeks once build work begins. Timelines vary based on engineering prioritization, security and change-management processes, data readiness, and whether a migration is required.`}</p>
      <h2 {...{
        "id": "token-model"
      }}>{`Token model`}</h2>
      <p>{`Core Exchange integrations use a user-scoped access token (sometimes referred to as an "aggregator token" or "single token") by default.`}</p>
      <p>{`Why this model is used:`}</p>
      <ul>
        <li parentName="ul">{`It enables a consistent user experience across Plaid-powered applications by allowing prior authorizations to be reused when appropriate.`}</li>
        <li parentName="ul">{`It simplifies token management by avoiding per-application token issuance while maintaining centralized consent enforcement.`}</li>
        <li parentName="ul">{`It establishes Plaid as the sole OAuth client interacting with your API, reducing integration complexity.`}</li>
      </ul>
      <p>{`How it works:`}</p>
      <ul>
        <li parentName="ul">{`A single access token is issued to Plaid for a given end user.`}</li>
        <li parentName="ul">{`Plaid is the only entity that uses this token to call your Core Exchange endpoints.`}</li>
        <li parentName="ul">{`Downstream applications do not receive or present your access token directly; Plaid retrieves data on their behalf based on authorized permissions.`}</li>
      </ul>
      <p>{`This token model is the default for Core Exchange implementations.`}</p>
      <p>{`For more details, see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/user-experience/#returning-user-experience"
        }}>{`Returning user experience`}</a>{`.`}</p>
      <h2 {...{
        "id": "integration-models-single-institution-vs-platform"
      }}>{`Integration models: single institution vs platform`}</h2>
      <p>{`When onboarding in the Data Partner Dashboard, you must select the correct integration model during the Basic Information step. This choice determines how configuration, testing, and ongoing management work and cannot be easily changed later.`}</p>
      <p>{`Core Exchange supports two onboarding models. While the APIs are the same, the operational lifecycle differs.`}</p>
      <h3 {...{
        "id": "single-institution"
      }}>{`Single institution`}</h3>
      <ul>
        <li parentName="ul">{`One Data Partner Dashboard account`}</li>
        <li parentName="ul">{`One institution configuration`}</li>
        <li parentName="ul">{`Configuration managed per environment (for example, Development and Production)`}</li>
        <li parentName="ul">{`One testing and production launch`}</li>
        <li parentName="ul">{`No-code `}<a parentName="li" {...{
            "href": "/core-exchange/docs/consent-management"
          }}>{`Consent management`}</a>{` and App Directory tools`}</li>
      </ul>
      <h3 {...{
        "id": "platform"
      }}>{`Platform`}</h3>
      <ul>
        <li parentName="ul">{`One platform-level Dashboard account`}</li>
        <li parentName="ul">{`Multiple institutions configured and launched over time`}</li>
        <li parentName="ul">{`Configuration managed per institution rather than per environment`}</li>
        <li parentName="ul">{`Repeated testing and launch cycles for each institution`}</li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/core-exchange/docs/consent-management"
          }}>{`Consent management`}</a>{` and App Directory available via API only`}</li>
      </ul>
      <p>{`Platform-specific considerations are called out throughout this guide.`}</p>
      <h2 {...{
        "id": "before-you-start"
      }}>{`Before you start`}</h2>
      <p>{`Before building and testing your Core Exchange integration, ensure your Dashboard profile is complete.`}</p>
      <p>{`This includes:`}</p>
      <ul>
        <li parentName="ul">{`Basic organization and institution information`}</li>
        <li parentName="ul">{`Business details`}</li>
        <li parentName="ul">{`Technical contact information`}</li>
      </ul>
      <p>{`Some Dashboard functionality may remain unavailable until required profile steps are completed. Information provided during profile completion is reviewed by Plaid's risk team as part of onboarding.`}</p>
      <p>{`For more details, see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/implementation-checklist"
        }}>{`Implementation checklist`}</a>{`.`}</p>
      <h2 {...{
        "id": "managing-team-access"
      }}>{`Managing team access`}</h2>
      <p>{`Administrators can manage team access directly from the Data Partner Dashboard.`}</p>
      <ul>
        <li parentName="ul">{`Navigate to `}<strong parentName="li">{`Settings`}</strong>{` → `}<strong parentName="li">{`Members`}</strong>{` to invite teammates`}</li>
        <li parentName="ul">{`Assign granular permissions based on each user's role`}</li>
      </ul>
      <h2 {...{
        "id": "getting-set-up-in-the-data-partner-dashboard"
      }}>{`Getting set up in the Data Partner Dashboard`}</h2>
      <p>{`Your integration journey begins in the Plaid Data Partner Dashboard. This is where you configure, test, validate, and request production access for Core Exchange.`}</p>
      <p>{`Plaid provisions your initial Dashboard access. From there, most onboarding steps are self-serve, with Plaid available to help as questions arise.`}</p>
      <p>{`From the Dashboard, you can:`}</p>
      <ul>
        <li parentName="ul">{`Configure OAuth and API settings`}</li>
        <li parentName="ul">{`Choose which backend endpoints Plaid should call`}</li>
        <li parentName="ul">{`Configure branding shown in Plaid Link`}</li>
        <li parentName="ul">{`Validate endpoints individually or end to end`}</li>
        <li parentName="ul">{`Inspect request and response payloads`}</li>
        <li parentName="ul">{`Preview how your data appears in Plaid-powered apps`}</li>
        <li parentName="ul">{`View validation results and error logs`}</li>
      </ul>
      <p>{`Configuration depends on your integration model.`}</p>
      <p>{`For single-institution dashboards, you can configure multiple environments (for example, Development, Staging, and Production), each pointing to different backend endpoints.`}</p>
      <p>{`For platform dashboards, environments are not used. Each institution entry represents a distinct configuration. Partners often create separate institution records for development, testing, and production use cases.`}</p>
      <p>{`At this stage:`}</p>
      <ul>
        <li parentName="ul">{`No production traffic is enabled`}</li>
        <li parentName="ul">{`Your institution is not discoverable to end users`}</li>
        <li parentName="ul">{`All testing is housed within the Dashboard`}</li>
      </ul>
      <p>{`For more details, see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/dashboard-overview"
        }}>{`Dashboard overview`}</a>{`.`}</p>
      <h2 {...{
        "id": "account-select-behavior"
      }}>{`Account select behavior`}</h2>
      <p>{`Account Select is the step in Plaid Link where users choose which accounts to share. Although it appears in Link, this behavior is controlled by the third-party application using Plaid, not by Plaid or the data partner.`}</p>
      <p>{`Applications may support single account selection, multiple account selection, or all accounts preselected, depending on the use case.`}</p>
      <p>{`When testing in the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/testing"
        }}>{`Validator`}</a>{`, Account Select is set to `}<em parentName="p">{`all accounts`}</em>{` preselected to exercise as much data as possible.`}</p>
      <p>{`For more details, see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/user-experience/#account-selection"
        }}>{`Account selection`}</a>{`.`}</p>
      <h2 {...{
        "id": "configuring-plaid-products"
      }}>{`Configuring Plaid products`}</h2>
      <p>{`Plaid products rely on different account types and data capabilities. Not every product applies to every integration.`}</p>
      <p>{`Product configuration depends on supported account types such as depository, credit, loan, or investment accounts.`}</p>
      <p>{`If certain account types or data are not supported, corresponding products should be disabled. Product enablement can be adjusted over time in coordination with Plaid.`}</p>
      <p>{`After launch, integrations can be expanded by enabling additional account types or adding new products. New capabilities can be validated and rolled out incrementally without a full re-launch.`}</p>
      <h2 {...{
        "id": "migrations"
      }}>{`Migrations`}</h2>
      <p>{`Plaid will work with you on a structured migration plan designed to minimize disruption to existing Plaid-connected users.`}</p>
      <p>{`Typical migrations run over approximately 90 days, gradually ramp traffic, and require users to re-authenticate. In select cases, `}<a parentName="p" {...{
          "href": "/core-exchange/docs/migrating-from-plaid-exchange/backend-token-exchange"
        }}>{`backend token exchange`}</a>{` may be available to migrate connections without requiring user reauthentication.`}</p>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "about-core-exchange",
        level: 2,
        title: "About Core Exchange",
      },
{
        id: "the-high-level-journey",
        level: 2,
        title: "The high-level journey",
      },
{
        id: "onboarding-and-setup",
        level: 3,
        title: "Onboarding and setup",
      },
{
        id: "build-and-test",
        level: 3,
        title: "Build and test",
      },
{
        id: "launch",
        level: 3,
        title: "Launch",
      },
{
        id: "post-launch",
        level: 3,
        title: "Post-launch",
      },
{
        id: "token-model",
        level: 2,
        title: "Token model",
      },
{
        id: "integration-models-single-institution-vs-platform",
        level: 2,
        title: "Integration models: single institution vs platform",
      },
{
        id: "single-institution",
        level: 3,
        title: "Single institution",
      },
{
        id: "platform",
        level: 3,
        title: "Platform",
      },
{
        id: "before-you-start",
        level: 2,
        title: "Before you start",
      },
{
        id: "managing-team-access",
        level: 2,
        title: "Managing team access",
      },
{
        id: "getting-set-up-in-the-data-partner-dashboard",
        level: 2,
        title: "Getting set up in the Data Partner Dashboard",
      },
{
        id: "account-select-behavior",
        level: 2,
        title: "Account select behavior",
      },
{
        id: "configuring-plaid-products",
        level: 2,
        title: "Configuring Plaid products",
      },
{
        id: "migrations",
        level: 2,
        title: "Migrations",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
